import type { CSSProperties } from 'react';
import type { AccentColor } from '@/stores/app-store';

export type PortalThemeTemplate =
  | 'haulier'
  | 'transport'
  | 'logistics'
  | 'warehouse'
  | 'total_logistics'
  | 'freight_forwarder';

export type ThemePaletteId =
  | 'ocean'
  | 'signal_blue'
  | 'teal'
  | 'emerald'
  | 'amber'
  | 'coral'
  | 'rose'
  | 'slate';

export type VisualPatternId =
  | 'port_terminal'
  | 'rose_logistics'
  | 'warehouse_grid'
  | 'container_haulage'
  | 'control_tower'
  | 'executive_slate'
  | 'coral_service'
  | 'minimal_grid'
  | 'smart_inbox'
  | 'customs_docs'
  | 'depot_gate'
  | 'route_atlas'
  | 'cold_chain'
  | 'air_cargo'
  | 'ledger_flow'
  | 'ai_network';

export type ThemeIntensity = 'low' | 'medium' | 'high';
export type ThemeMotion = 'off' | 'subtle';
export type AssistantPersonaId = 'boox_bot' | 'port_pilot' | 'yard_ranger' | 'ledger_keeper' | 'lion_cub';
export type GamificationMode = 'off' | 'subtle' | 'training' | 'playful';

export interface BrandThemeConfig {
  palette?: ThemePaletteId;
  visualPattern?: VisualPatternId;
  intensity?: ThemeIntensity;
  motion?: ThemeMotion;
  assistantPersona?: AssistantPersonaId;
  gamificationMode?: GamificationMode;
}

export interface ResolvedBrandTheme {
  palette: ThemePaletteId;
  visualPattern: VisualPatternId;
  intensity: ThemeIntensity;
  motion: ThemeMotion;
  assistantPersona: AssistantPersonaId;
  gamificationMode: GamificationMode;
}

export interface ThemePalette {
  id: ThemePaletteId;
  label: string;
  description: string;
  preview: string;
  surface: string;
  surfaceStrong: string;
  motifMain: string;
  motifSoft: string;
  motifMuted: string;
  accent: string;
  accentText: string;
  text: string;
}

export interface PortalThemeOption {
  value: PortalThemeTemplate;
  label: string;
  description: string;
  sector: string;
  preview: string;
  defaultBrandTheme: ResolvedBrandTheme;
}

export interface PortalThemeDefinition extends PortalThemeOption {
  shellStyle: CSSProperties;
  textureStyle: CSSProperties;
  cardStyle: CSSProperties;
  badgeStyle: CSSProperties;
}

export interface ThemeChoiceOption<T extends string> {
  value: T;
  label: string;
  description: string;
  recommendedFor?: string;
}

export const THEME_PALETTES: Record<ThemePaletteId, ThemePalette> = {
  ocean: {
    id: 'ocean',
    label: 'Ocean',
    description: 'Navy and cyan for freight forwarding, port and shipping brands.',
    preview: 'from-slate-950 via-blue-800 to-cyan-500',
    surface: '#eef6ff',
    surfaceStrong: '#0b1838',
    motifMain: '#1f398d',
    motifSoft: '#29b8da',
    motifMuted: '#93c5fd',
    accent: '#29b8da',
    accentText: '#ffffff',
    text: '#0b1838',
  },
  signal_blue: {
    id: 'signal_blue',
    label: 'Signal Blue',
    description: 'Clean SaaS blue for general workspace and default operational surfaces.',
    preview: 'from-blue-900 via-blue-600 to-sky-400',
    surface: '#eff6ff',
    surfaceStrong: '#1e3a8a',
    motifMain: '#1d4ed8',
    motifSoft: '#0ea5e9',
    motifMuted: '#bfdbfe',
    accent: '#2563eb',
    accentText: '#ffffff',
    text: '#172554',
  },
  teal: {
    id: 'teal',
    label: 'Teal',
    description: 'Teal and sea green for 3PL, logistics and routing-heavy tenants.',
    preview: 'from-teal-950 via-teal-700 to-cyan-400',
    surface: '#ecfeff',
    surfaceStrong: '#0f3f3b',
    motifMain: '#0f766e',
    motifSoft: '#14b8a6',
    motifMuted: '#99f6e4',
    accent: '#0f766e',
    accentText: '#ffffff',
    text: '#134e4a',
  },
  emerald: {
    id: 'emerald',
    label: 'Emerald',
    description: 'Green warehouse palette for WMS, inventory and cold-chain workflows.',
    preview: 'from-emerald-950 via-emerald-700 to-green-400',
    surface: '#ecfdf5',
    surfaceStrong: '#064e3b',
    motifMain: '#047857',
    motifSoft: '#22c55e',
    motifMuted: '#bbf7d0',
    accent: '#059669',
    accentText: '#ffffff',
    text: '#064e3b',
  },
  amber: {
    id: 'amber',
    label: 'Amber',
    description: 'Warm amber for haulage, depot, fleet and yard operations.',
    preview: 'from-stone-900 via-amber-700 to-yellow-400',
    surface: '#fffbeb',
    surfaceStrong: '#451a03',
    motifMain: '#92400e',
    motifSoft: '#f59e0b',
    motifMuted: '#fde68a',
    accent: '#d97706',
    accentText: '#ffffff',
    text: '#451a03',
  },
  coral: {
    id: 'coral',
    label: 'Coral',
    description: 'Friendly warm coral for customer service and external portals.',
    preview: 'from-orange-900 via-orange-600 to-orange-300',
    surface: '#fff7ed',
    surfaceStrong: '#7c2d12',
    motifMain: '#c2410c',
    motifSoft: '#fb923c',
    motifMuted: '#fed7aa',
    accent: '#ea580c',
    accentText: '#ffffff',
    text: '#7c2d12',
  },
  rose: {
    id: 'rose',
    label: 'Rose',
    description: 'Soft rose palette for personal workspace and warmer CRM surfaces.',
    preview: 'from-rose-950 via-rose-600 to-pink-300',
    surface: '#fff1f2',
    surfaceStrong: '#881337',
    motifMain: '#be123c',
    motifSoft: '#fb7185',
    motifMuted: '#fecdd3',
    accent: '#e11d48',
    accentText: '#ffffff',
    text: '#881337',
  },
  slate: {
    id: 'slate',
    label: 'Slate',
    description: 'Quiet steel palette for finance, audit and executive views.',
    preview: 'from-slate-950 via-slate-600 to-slate-300',
    surface: '#f8fafc',
    surfaceStrong: '#0f172a',
    motifMain: '#334155',
    motifSoft: '#64748b',
    motifMuted: '#cbd5e1',
    accent: '#475569',
    accentText: '#ffffff',
    text: '#0f172a',
  },
};

export const THEME_PALETTE_OPTIONS: Array<ThemeChoiceOption<ThemePaletteId> & { preview: string }> =
  Object.values(THEME_PALETTES).map((palette) => ({
    value: palette.id,
    label: palette.label,
    description: palette.description,
    preview: palette.preview,
  }));

export const VISUAL_PATTERN_OPTIONS: Array<ThemeChoiceOption<VisualPatternId>> = [
  { value: 'port_terminal', label: 'Port Terminal', description: 'Ship, quay crane, yard stacks and sea routes.', recommendedFor: 'Freight / port / shipping' },
  { value: 'rose_logistics', label: 'Rose Logistics', description: 'Soft route curves with rose line-art and container marks.', recommendedFor: 'Personal workspace / soft onboarding' },
  { value: 'warehouse_grid', label: 'Warehouse Grid', description: 'Rack grid, pallets, scanner and warehouse routes.', recommendedFor: 'WMS / fulfillment' },
  { value: 'container_haulage', label: 'Container Haulage', description: 'Truck, chassis, lane marks and gate movement.', recommendedFor: 'Haulier / depot' },
  { value: 'control_tower', label: 'Control Tower', description: 'Signals, route intelligence and command nodes.', recommendedFor: 'AI ops / management' },
  { value: 'executive_slate', label: 'Executive Slate', description: 'Dashboards, summaries and low-noise chart blocks.', recommendedFor: 'Finance / executive' },
  { value: 'coral_service', label: 'Coral Service', description: 'Support cards, status bubbles and request flow.', recommendedFor: 'Customer portal' },
  { value: 'minimal_grid', label: 'Minimal Grid', description: 'Faint grid, route line and container marks.', recommendedFor: 'Sidebar / dense workspace' },
  { value: 'smart_inbox', label: 'Smart Inbox', description: 'Email, document and AI extraction path.', recommendedFor: 'Communication / intake' },
  { value: 'customs_docs', label: 'Customs Docs', description: 'Documents, stamp and approval check.', recommendedFor: 'Compliance / customs' },
  { value: 'depot_gate', label: 'Depot Gate', description: 'Gate, EIR and truck checkpoint.', recommendedFor: 'Depot / yard' },
  { value: 'route_atlas', label: 'Route Atlas', description: 'Map grid, route arcs and stop nodes.', recommendedFor: 'Transport planning' },
  { value: 'cold_chain', label: 'Cold Chain', description: 'Reefer truck, snowflake and sensor route.', recommendedFor: 'Special cargo' },
  { value: 'air_cargo', label: 'Air Cargo', description: 'Aircraft path and cargo boxes.', recommendedFor: 'Air freight' },
  { value: 'ledger_flow', label: 'Ledger Flow', description: 'AR/AP documents, posting arrow and matching.', recommendedFor: 'Finance' },
  { value: 'ai_network', label: 'AI Network', description: 'Skill graph, approval traces and agent nodes.', recommendedFor: 'AI agent / training' },
];

export const ASSISTANT_PERSONA_OPTIONS: Array<ThemeChoiceOption<AssistantPersonaId>> = [
  { value: 'boox_bot', label: 'Boox Bot', description: 'Default professional AI workflow coach.', recommendedFor: 'Enterprise default' },
  { value: 'port_pilot', label: 'Port Pilot', description: 'Shipping and port-oriented guide.', recommendedFor: 'Freight / forwarding' },
  { value: 'yard_ranger', label: 'Yard Ranger', description: 'Warehouse, depot and field execution guide.', recommendedFor: 'WMS / depot' },
  { value: 'ledger_keeper', label: 'Ledger Keeper', description: 'Finance workflow and audit guard.', recommendedFor: 'Finance' },
  { value: 'lion_cub', label: 'Lion Cub', description: 'Singapore-origin playful coach for training and personal workspace.', recommendedFor: 'Training / demo' },
];

export const GAMIFICATION_MODE_OPTIONS: Array<ThemeChoiceOption<GamificationMode>> = [
  { value: 'off', label: 'Off', description: 'No workflow game cues.' },
  { value: 'subtle', label: 'Subtle', description: 'Production-safe progress, checklist and completion cues.' },
  { value: 'training', label: 'Training', description: 'Guided steps, badges and onboarding feedback.' },
  { value: 'playful', label: 'Playful', description: 'Persona reactions and lightweight achievements for demo or personal use.' },
];

export const THEME_INTENSITY_OPTIONS: Array<ThemeChoiceOption<ThemeIntensity>> = [
  { value: 'low', label: 'Low', description: 'Best for sidebar and dense work surfaces.' },
  { value: 'medium', label: 'Medium', description: 'Best for login and portal hero surfaces.' },
  { value: 'high', label: 'High', description: 'Best for demo, onboarding and public hero moments.' },
];

const DEFAULT_BRAND_THEMES: Record<PortalThemeTemplate, ResolvedBrandTheme> = {
  haulier: {
    palette: 'amber',
    visualPattern: 'container_haulage',
    intensity: 'medium',
    motion: 'off',
    assistantPersona: 'yard_ranger',
    gamificationMode: 'subtle',
  },
  transport: {
    palette: 'signal_blue',
    visualPattern: 'route_atlas',
    intensity: 'medium',
    motion: 'off',
    assistantPersona: 'boox_bot',
    gamificationMode: 'subtle',
  },
  logistics: {
    palette: 'teal',
    visualPattern: 'route_atlas',
    intensity: 'medium',
    motion: 'off',
    assistantPersona: 'boox_bot',
    gamificationMode: 'subtle',
  },
  warehouse: {
    palette: 'emerald',
    visualPattern: 'warehouse_grid',
    intensity: 'medium',
    motion: 'off',
    assistantPersona: 'yard_ranger',
    gamificationMode: 'subtle',
  },
  total_logistics: {
    palette: 'signal_blue',
    visualPattern: 'control_tower',
    intensity: 'medium',
    motion: 'off',
    assistantPersona: 'boox_bot',
    gamificationMode: 'subtle',
  },
  freight_forwarder: {
    palette: 'ocean',
    visualPattern: 'route_atlas',
    intensity: 'medium',
    motion: 'off',
    assistantPersona: 'port_pilot',
    gamificationMode: 'subtle',
  },
};

const THEMES: Record<PortalThemeTemplate, PortalThemeDefinition> = {
  haulier: {
    value: 'haulier',
    label: 'Haulier',
    description: 'Amber lane stripes for haulage and depot workflows.',
    sector: 'Haulier',
    preview: THEME_PALETTES.amber.preview,
    defaultBrandTheme: DEFAULT_BRAND_THEMES.haulier,
    shellStyle: { background: 'linear-gradient(135deg, #1a1817 0%, #603d1a 45%, #bf863f 100%)' },
    textureStyle: { backgroundImage: 'repeating-linear-gradient(135deg, rgba(255,255,255,0.08) 0 16px, transparent 16px 38px)', opacity: 0.85 },
    cardStyle: { background: 'rgba(255,248,235,0.9)', border: '1px solid rgba(255,255,255,0.18)' },
    badgeStyle: { background: 'rgba(255,247,237,0.18)', border: '1px solid rgba(255,255,255,0.18)' },
  },
  transport: {
    value: 'transport',
    label: 'Transport',
    description: 'Blue motion lines for fleet, route and transport portals.',
    sector: 'Transport',
    preview: THEME_PALETTES.signal_blue.preview,
    defaultBrandTheme: DEFAULT_BRAND_THEMES.transport,
    shellStyle: { background: 'linear-gradient(135deg, #101d35 0%, #165084 45%, #28c1ee 100%)' },
    textureStyle: { backgroundImage: 'linear-gradient(120deg, rgba(255,255,255,0.10) 0%, transparent 38%), repeating-linear-gradient(0deg, rgba(255,255,255,0.08) 0 3px, transparent 3px 22px)', opacity: 0.85 },
    cardStyle: { background: 'rgba(239,248,255,0.16)', border: '1px solid rgba(255,255,255,0.18)' },
    badgeStyle: { background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.18)' },
  },
  logistics: {
    value: 'logistics',
    label: 'Logistics',
    description: 'Teal routing grid for 3PL and modern logistics tenants.',
    sector: 'Logistics',
    preview: THEME_PALETTES.teal.preview,
    defaultBrandTheme: DEFAULT_BRAND_THEMES.logistics,
    shellStyle: { background: 'linear-gradient(135deg, #0e2924 0%, #146452 45%, #3fc0d4 100%)' },
    textureStyle: { backgroundImage: 'linear-gradient(rgba(255,255,255,0.10) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)', backgroundSize: '30px 30px', opacity: 0.75 },
    cardStyle: { background: 'rgba(239,253,249,0.16)', border: '1px solid rgba(255,255,255,0.18)' },
    badgeStyle: { background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.18)' },
  },
  warehouse: {
    value: 'warehouse',
    label: 'Warehouse',
    description: 'Emerald warehouse grid for storage and fulfillment workflows.',
    sector: 'Warehouse',
    preview: THEME_PALETTES.emerald.preview,
    defaultBrandTheme: DEFAULT_BRAND_THEMES.warehouse,
    shellStyle: { background: 'linear-gradient(135deg, #0f3327 0%, #047857 46%, #22c55e 100%)' },
    textureStyle: { backgroundImage: 'linear-gradient(rgba(255,255,255,0.10) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)', backgroundSize: '24px 24px', opacity: 0.75 },
    cardStyle: { background: 'rgba(239,253,244,0.16)', border: '1px solid rgba(255,255,255,0.18)' },
    badgeStyle: { background: 'rgba(240,253,244,0.16)', border: '1px solid rgba(255,255,255,0.18)' },
  },
  total_logistics: {
    value: 'total_logistics',
    label: 'Total Logistics',
    description: 'Control tower network layers for end-to-end logistics groups.',
    sector: 'Total Logistics',
    preview: THEME_PALETTES.signal_blue.preview,
    defaultBrandTheme: DEFAULT_BRAND_THEMES.total_logistics,
    shellStyle: { background: 'linear-gradient(135deg, #171935 0%, #37348c 45%, #3cc4ea 100%)' },
    textureStyle: { backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.14) 0 2px, transparent 2px), radial-gradient(circle at 80% 30%, rgba(255,255,255,0.12) 0 2px, transparent 2px)', backgroundSize: '52px 52px, 68px 68px', opacity: 0.8 },
    cardStyle: { background: 'rgba(242,245,255,0.16)', border: '1px solid rgba(255,255,255,0.18)' },
    badgeStyle: { background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.18)' },
  },
  freight_forwarder: {
    value: 'freight_forwarder',
    label: 'Freight Forwarder',
    description: 'Navy and ocean palette for forwarding and NVOCC brands.',
    sector: 'Freight Forwarder',
    preview: THEME_PALETTES.ocean.preview,
    defaultBrandTheme: DEFAULT_BRAND_THEMES.freight_forwarder,
    shellStyle: { background: 'linear-gradient(135deg, #0b1838 0%, #1f398d 45%, #29b8da 100%)' },
    textureStyle: { backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.12) 1px, transparent 1px)', backgroundSize: '24px 24px', opacity: 0.72 },
    cardStyle: { background: 'rgba(239,244,255,0.16)', border: '1px solid rgba(255,255,255,0.18)' },
    badgeStyle: { background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.18)' },
  },
};

export const PORTAL_THEME_OPTIONS: PortalThemeOption[] = Object.values(THEMES).map((theme) => ({
  value: theme.value,
  label: theme.label,
  description: theme.description,
  sector: theme.sector,
  preview: theme.preview,
  defaultBrandTheme: theme.defaultBrandTheme,
}));

export function resolvePortalTheme(template?: string | null): PortalThemeDefinition {
  if (!template) return THEMES.freight_forwarder;
  return THEMES[template as PortalThemeTemplate] ?? THEMES.freight_forwarder;
}

export function resolveBrandTheme(template?: string | null, override?: BrandThemeConfig | null): ResolvedBrandTheme {
  const base = resolvePortalTheme(template).defaultBrandTheme;
  if (!override) return base;
  return {
    palette: override.palette ?? base.palette,
    visualPattern: override.visualPattern ?? base.visualPattern,
    intensity: override.intensity ?? base.intensity,
    motion: override.motion ?? base.motion,
    assistantPersona: override.assistantPersona ?? base.assistantPersona,
    gamificationMode: override.gamificationMode ?? base.gamificationMode,
  };
}

export function resolveThemePalette(palette?: ThemePaletteId | null): ThemePalette {
  const fallback = THEME_PALETTES.ocean;
  if (!fallback) throw new Error('Theme palettes are not configured');
  return THEME_PALETTES[palette ?? 'ocean'] ?? fallback;
}

export function accentToThemePalette(accent: AccentColor): ThemePaletteId {
  const map: Record<AccentColor, ThemePaletteId> = {
    blue: 'signal_blue',
    rose: 'rose',
    pink: 'rose',
    purple: 'signal_blue',
    teal: 'teal',
    green: 'emerald',
    amber: 'amber',
    indigo: 'signal_blue',
    orange: 'coral',
    slate: 'slate',
  };
  return map[accent] ?? 'signal_blue';
}
